import { Box, Card, Grid, SxProps } from '@mui/material';
import { FC } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import { useIntl } from 'react-intl';
import { EmployeeType } from '../../utils/type';
import { ActivitySheet } from '../../services/configApi/activitySheet/weeklyActivitySheetProvider';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import {
  formatNumberToDecimal,
  formatNumberToDecimalString,
} from '../../utils/utilities';

interface ActivityWeekPeekProps {
  activity: ActivitySheet;
  employeeType: EmployeeType | null;
  cardStyle?: SxProps;
}

const ActivitySheetWeekPeekNew: FC<ActivityWeekPeekProps> = ({
  cardStyle,
  activity,
  employeeType,
}) => {
  const rctl = useIntl();

  const calculateProductivityHoursFromPercentage = () => {
    const totalWorkingHours = activity.totalWorkingHoursDec ?? 0;
    const productivityGoal = activity.productivityGoal ?? '0.00';

    const productiveHours =
      totalWorkingHours * (Number(productivityGoal) / 100);
    return productiveHours.toFixed(2);
  };

  const calculateTotalActivityValue = () => {
    if (activity.employeeType === 'Hourly Non-Exempt') {
      const completedBillableHours = activity.totalBillableHoursDec ?? 0;
      const totalApprovedNonBillableHoursDec =
        activity.totalApprovedNonBillableHoursDec ?? 0.0;
      const noShowTravelDocHours = activity.totalNoShowTravelDocHoursDec ?? 0;
      const otherAssocWorkHrs = activity.totalOtherAssocWorkHrsDec ?? 0.0;
      const nonBillableHours = activity.totalOtherNonBillableHrsDec ?? 0.0;

      return formatNumberToDecimalString(
        completedBillableHours +
          totalApprovedNonBillableHoursDec +
          noShowTravelDocHours +
          otherAssocWorkHrs +
          nonBillableHours,
      );
    } else {
      const completedBillableHours = activity.totalBillableHoursDec ?? 0;
      const totalOtherNonBillableHrs =
        activity.totalOtherNonBillableHrsDec ?? 0.0;
      const totalApprovedNonBillableHoursDec =
        activity.totalApprovedNonBillableHoursDec ?? 0.0;
      let otherAssocWorkHrs = 0;
      if (employeeType !== 'Salaried Exempt') {
        otherAssocWorkHrs = activity.totalOtherAssocWorkHrsDec ?? 0.0;
      }

      return formatNumberToDecimalString(
        completedBillableHours +
          totalApprovedNonBillableHoursDec +
          totalOtherNonBillableHrs +
          otherAssocWorkHrs,
      );
    }
  };

  const getProductivityPercentage = () => {
    return activity.currentWeekProductivityPercent ?? '00:00';
  };

  const getProductivityPercentageHours = () => {
    const totalScheduledBillableHours = activity.totalBillableHoursDec ?? 0;
    const totalApprovedNonBillableHoursDec =
      activity.totalApprovedNonBillableHoursDec ?? 0.0;

    return formatNumberToDecimalString(
      totalScheduledBillableHours + totalApprovedNonBillableHoursDec,
    );
  };

  const checkHoursOutApplied = () => {
    const hourProperties = [
      'totalVacationHoursDec',
      'totalEtHoursDec',
      'totalFlexHoursDec',
      'totalProfessionalHoursDec',
      'totalHolidayHoursDec',
      'totalSickHoursDec',
      'totalLtiHoursDec',
      'totalJuryDutyHoursDec',
      'totalPfmlHoursDec',
      'totalHourlyTimeOffHoursDec',
      'totalBereaveHoursDec',
    ] as const;

    return hourProperties.every(
      (prop) => activity[prop] === null || activity[prop] === 0,
    );
  };

  return (
    <Box sx={{ ...cardStyle }}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Card>
            <Grid container className={'p-md'} spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Box>
                  <Box className={'flex__'} justifyContent={'center'}>
                    <MediumTypography
                      className="mainText-xlg pr-xs"
                      textColor="var(--thom-green-primary2)"
                      label={`${getProductivityPercentageHours()} hrs`}
                    />
                    <MediumTypography
                      className="mainText-xlg pr-xs"
                      textColor="var(--thom-green-primary2)"
                      label={`(${getProductivityPercentage()}%)`}
                    />
                  </Box>
                  <Box className={'flex__ mt-xs'} justifyContent={'center'}>
                    <MediumTypography
                      label={`${rctl.formatMessage({
                        id: 'productivityGoalText',
                        defaultMessage: 'Productivity Goal(%)',
                      })} : `}
                    />
                    <MediumTypography
                      className="ml-xs mr-xs"
                      label={calculateProductivityHoursFromPercentage()}
                    />
                    <MediumTypography
                      label={`(${
                        activity.productivityGoal !== null &&
                        activity.productivityGoal !== ''
                          ? activity.productivityGoal
                          : '0.00'
                      }%)`}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <Grid container className={'p-md'} alignItems={'center'}>
              <Grid item xs={2.5} display={'flex'}>
                <Box flexGrow={1}>
                  <MediumTypography
                    textstyle="center"
                    className="mainText-xlg pr-xs"
                    textColor="var(--thom-green-primary2)"
                    label={`${calculateTotalActivityValue()} hrs`}
                  />
                  <MediumTypography
                    className="mainText-md mt-xs"
                    textstyle={'center'}
                    labelid="totalActivityText"
                    defaultlabel={'Total Activity'}
                  />
                </Box>
                <Box
                  sx={{
                    marginRight: '16px',
                    width: '1px',
                    backgroundColor: 'var(--thom-mono-gray2)',
                  }}
                />
              </Grid>

              <Grid item xs={9.5}>
                <Grid container columnSpacing={2} rowSpacing={0.5}>
                  {activity.totalBillableHoursDec !== null && (
                    <Grid item display={'flex'}>
                      <MediumTypography
                        className="mainText-md mr-xs"
                        label={`${rctl.formatMessage({
                          id: 'billableHoursCompletedText',
                          defaultMessage: 'Completed Billable Hours',
                        })} : `}
                      />
                      <MediumTypography
                        label={
                          formatNumberToDecimalString(
                            activity.totalBillableHoursDec,
                          ) + ' hrs'
                        }
                        textColor="var(--thom-green-primary2)"
                      />
                    </Grid>
                  )}

                  {activity.totalApprovedNonBillableHoursDec !== null && (
                    <Grid item display={'flex'}>
                      <MediumTypography
                        className="mainText-md mr-xs"
                        label={`${rctl.formatMessage({
                          id: 'approvedNonBillableHoursText',
                          defaultMessage: 'Approved Non-Billable Hours',
                        })} : `}
                      />
                      <MediumTypography
                        label={
                          activity.totalApprovedNonBillableHoursDec === null
                            ? '0.00 hrs'
                            : `${formatNumberToDecimal(
                                activity.totalApprovedNonBillableHoursDec,
                              )} hrs`
                        }
                        textColor="var(--thom-green-primary2)"
                      />
                    </Grid>
                  )}

                  {employeeType &&
                    employeeType === 'Hourly Non-Exempt' &&
                    activity.totalNoShowTravelDocHoursDec !== null && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'noShowDescText',
                            defaultMessage:
                              'No Show (Travel, Wait, Doc, Other)',
                          })} : `}
                        />
                        <MediumTypography
                          label={
                            formatNumberToDecimalString(
                              activity.totalNoShowTravelDocHoursDec,
                            ) + ' hrs'
                          }
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {employeeType &&
                    employeeType !== 'Salaried Exempt' &&
                    activity.totalOtherAssocWorkHrsDec !== null && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'otherAssocWorkHoursText',
                            defaultMessage: 'Other Assoc. Work Hours',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${formatNumberToDecimal(
                            activity.totalOtherAssocWorkHrsDec,
                          )} hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalOtherNonBillableHrsDec !== null && (
                    <Grid item display={'flex'}>
                      <MediumTypography
                        className="mainText-md mr-xs"
                        label={`${rctl.formatMessage({
                          id: 'otherNonBillableHoursText',
                          defaultMessage: 'Other Non-Billable Hours',
                        })} : `}
                      />
                      <MediumTypography
                        label={`${formatNumberToDecimal(
                          activity.totalOtherNonBillableHrsDec,
                        )} hrs`}
                        textColor="var(--thom-green-primary2)"
                      />
                    </Grid>
                  )}
                </Grid>

                <Box className="horizontalLine mt-xs mb-xs" />

                <Grid container columnSpacing={2} rowSpacing={0.5}>
                  <Grid item display={'flex'}>
                    <MediumTypography
                      className="mainText-md mr-xs"
                      label={`${rctl.formatMessage({
                        id: 'billableHoursScheduledText',
                        defaultMessage: 'Scheduled Billable Hours',
                      })} : `}
                    />
                    <MediumTypography
                      label={
                        activity.totalScheduledBillableHoursDec === null
                          ? '0.00 hrs'
                          : formatNumberToDecimalString(
                              activity.totalScheduledBillableHoursDec,
                            ) + ' hrs'
                      }
                      className="font-bold"
                      textColor="var(--thom-blue)"
                    />
                    {activity.scheduledProductivity && (
                      <MediumTypography
                        className="font-bold"
                        label={`(${activity.scheduledProductivity})`}
                        textColor="var(--thom-blue)"
                      />
                    )}
                  </Grid>

                  {activity.totalPayHoursDec !== null && (
                    <Grid item display={'flex'} alignItems={'center'}>
                      <MediumTypography
                        className="mainText-md mr-xs"
                        label={`${rctl.formatMessage({
                          id: 'totalHoursText',
                          defaultMessage: 'Total Hours',
                        })} : `}
                      />
                      <MediumTypography
                        label={
                          formatNumberToDecimalString(
                            activity.totalPayHoursDec,
                          ) + ' hrs'
                        }
                        className="font-bold"
                        textColor="var(--thom-blue)"
                      />
                      <ActionToolTip
                        labelId={
                          activity.newTPHCalculated
                            ? 'totalHoursHintTextNewTPHCalculated'
                            : 'totalHoursHintText'
                        }
                        defaultLabel="Total Hours added per Day by employee"
                      >
                        <InfoIcon
                          stroke="var(--thom-blue)"
                          className="ml-xs font-bold"
                          style={{
                            cursor: 'pointer',
                          }}
                        />
                      </ActionToolTip>
                    </Grid>
                  )}
                  {activity.flexHoursAllowedDec !== null && (
                    <Grid item display={'flex'}>
                      <MediumTypography
                        className="mainText-md mr-xs"
                        label={`${rctl.formatMessage({
                          id: 'flexHoursAllowed',
                          defaultMessage: 'Flex Hours Allowed',
                        })} : `}
                      />
                      <MediumTypography
                        label={`${formatNumberToDecimal(
                          activity.flexHoursAllowedDec,
                        )} hrs`}
                        className="font-bold"
                        textColor={
                          activity.flexHoursAllowedDec < 0
                            ? 'var(--thom-red)'
                            : 'var(--thom-blue)'
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <Grid container className={'p-md'} alignItems={'center'}>
              <Grid item xs={2.5} display={'flex'}>
                <Box flexGrow={1}>
                  <Box className={'flex__ justifyContent-Center'}>
                    <MediumTypography
                      className="mainText-xlg pr-xs"
                      textColor="var(--thom-green-primary2)"
                      label={`${
                        formatNumberToDecimalString(
                          activity.totalHoursOutDec,
                        ) ?? '0.00'
                      } hrs`}
                    />
                  </Box>
                  <MediumTypography
                    className="mainText-md mt-xs"
                    textstyle={'center'}
                    labelid="totalHoursOutText"
                    defaultlabel={'Total Hours Out'}
                  />
                </Box>
                <Box
                  sx={{
                    marginRight: '16px',
                    width: '1px',
                    backgroundColor: 'var(--thom-mono-gray2)',
                  }}
                />
              </Grid>

              {checkHoursOutApplied() && (
                <Grid item xs={9.5}>
                  <MediumTypography
                    label={`${rctl.formatMessage({
                      id: 'noHoursOutAppliedText',
                      defaultMessage: 'No Hours Out Applied',
                    })}`}
                  />
                </Grid>
              )}

              <Grid item xs={9.5}>
                <Grid container columnSpacing={1} rowSpacing={0.5}>
                  {activity.totalVacationHoursDec !== null &&
                    activity.totalVacationHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'vacationHoursText',
                            defaultMessage: 'Vacation',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalVacationHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalEtHoursDec !== null &&
                    activity.totalEtHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'etHoursText',
                            defaultMessage: 'Earned Time',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalEtHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalFlexHoursDec !== null &&
                    activity.totalFlexHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'flexHoursText',
                            defaultMessage: 'Flex',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalFlexHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalProfessionalHoursDec !== null &&
                    activity.totalProfessionalHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'profHoursText',
                            defaultMessage: 'Professional',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalProfessionalHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalHolidayHoursDec !== null &&
                    activity.totalHolidayHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'holidayTimeText',
                            defaultMessage: 'Holiday',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalHolidayHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalSickHoursDec !== null &&
                    activity.totalSickHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'sickHrsText',
                            defaultMessage: 'Sick/LTI',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalSickHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalLtiHoursDec !== null &&
                    activity.totalLtiHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'ltiHoursText',
                            defaultMessage: 'Sick Bank/Accumulated LTI',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalLtiHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalJuryDutyHoursDec !== null &&
                    activity.totalJuryDutyHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'juryDutyText',
                            defaultMessage: 'Jury Duty',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalJuryDutyHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalPfmlHoursDec !== null &&
                    activity.totalPfmlHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'pfmlfmlaShortText',
                            defaultMessage: 'PFML/FMLA',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalPfmlHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalBereaveHoursDec !== null &&
                    activity.totalBereaveHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'bereavementText',
                            defaultMessage: 'Bereavement',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalBereaveHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}

                  {activity.totalHourlyTimeOffHoursDec !== null &&
                    activity.totalHourlyTimeOffHoursDec !== 0 && (
                      <Grid item display={'flex'}>
                        <MediumTypography
                          className="mainText-md mr-xs"
                          label={`${rctl.formatMessage({
                            id: 'Hourlytimeoff',
                            defaultMessage: 'Hourly Time Off',
                          })} : `}
                        />
                        <MediumTypography
                          label={`${
                            formatNumberToDecimalString(
                              activity.totalHourlyTimeOffHoursDec,
                            ) ?? '0.00'
                          } hrs`}
                          textColor="var(--thom-green-primary2)"
                        />
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivitySheetWeekPeekNew;
